import React, { useEffect } from 'react';
import axios, { AxiosInstance } from 'axios';
import { useErrorContext } from './ErrorContext';
import { getAxiosInstance } from '../services/api/api.service';
import { storageService, StorageKeys } from '../services/storage.service';


export const setupAxiosInterceptors = (
  handleError: (message: string, source?: string) => void,
) => {

  getAxiosInstance().interceptors.response.use(
    (response) => {
      return response;
    },
    (error) => {
      const requestUrl = error.config?.url || 'Unknown URL';

      if (error.response) {
        // Handle auth errors (401/403)
        if (error.response.status === 401) {
          // Clear local storage auth data
          storageService.clearAuth();

          // Redirect to login page
          window.location.href = '/login';
          return Promise.reject(error);
        }

        if (error.response.status === 403) {
          // Redirect to forbidden page
          window.location.href = '/forbidden';
          return Promise.reject(error);
        }

        // Handle other API errors
        handleError(
          `API Error (${requestUrl}): ${error.response.status} - ${JSON.stringify(error.response.data)}`,
          'API Error',
        );
      } else if (error.request) {
        handleError(`No response from server (${requestUrl})`, 'Network Error');
      } else {
        handleError(
          `Request Error (${requestUrl}): ${error.message}`,
          'Request Error',
        );
      }
      return Promise.reject(error);
    },
  );

  // Add request interceptor to inject auth token
  getAxiosInstance().interceptors.request.use(
    (config) => {
      const token = storageService.getItem(StorageKeys.Token);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

};

interface ApiProviderParams {
  children: React.ReactNode;
}

export const ApiProvider: React.FC<ApiProviderParams> = ({ children }) => {
  const { addError } = useErrorContext();

  useEffect(() => {
    setupAxiosInterceptors(addError);
  }, [addError]);

  return <>{children}</>;
};
